import { Button } from '@bloomfieldrobotics/canopy';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PageviewIcon from '@mui/icons-material/Pageview';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Scan } from '../../../../services/entities/impl/blfApi/schemas/scan';
import { UploadDetail } from '../../../../services/entities/impl/blfApi/schemas/uploadSummary';
import { useTranslation } from '../../../../services/translation/hooks/useTranslation';
import { useScanCardEventHandlers } from '../../../scans/ScanSection/hooks/useScanCardEventHandlers';
import { CollectionSectionHeader } from './CollectionSectionHeader';
import { checkScanProcessingStatus } from './utils/checkScanProcessingStatus';
import { formatScanName } from './utils/formatScanName';
import { getPipelineStatusColor } from './utils/getPipelineStatusColor';
type CollectionScansOverviewProps = {
  scans: Scan[];
  uploadDetail?: UploadDetail;
};

export const CollectionScansOverview = ({
  scans,
  uploadDetail,
}: CollectionScansOverviewProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { handleOnClick } = useScanCardEventHandlers();

  const handleBlockDetailClick = (scan: Scan) => {
    handleOnClick(scan);
  };

  // Use the shared utility function to check scan processing status
  const scanProcessingResult = checkScanProcessingStatus(uploadDetail, scans);

  // Get the first message if there is one
  const scanProcessingMessage = scanProcessingResult.messages[0];

  return (
    <Paper variant='outlined' sx={{ p: 1.5, borderRadius: 1 }}>
      <CollectionSectionHeader
        icon={
          <PageviewIcon
            sx={(theme) => ({ color: theme.vars.palette.text.secondary })}
          />
        }
        title={t('Scans')}
        sx={{ mb: 1.5 }}
      />

      {scanProcessingMessage ? (
        <Alert
          severity={scanProcessingMessage.severity}
          icon={
            scanProcessingMessage.severity === 'error' ? (
              <ErrorOutlineIcon />
            ) : (
              <WarningAmberIcon />
            )
          }
          sx={{ mt: 1 }}
        >
          {scanProcessingMessage.message}
        </Alert>
      ) : scans.length === 0 ? (
        <Alert severity='info' sx={{ mt: 1 }}>
          {t(
            'No scans found yet. This is expected while Point Generation and Batch Processing are running.'
          )}
        </Alert>
      ) : (
        <Stack spacing={1.5}>
          {scans.map((scan) => (
            <Box
              key={scan.id}
              sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: theme.vars.palette.grey[800],
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: '1px solid',
                borderColor: theme.vars.palette.divider,
              }}
            >
              <Stack
                direction='row'
                alignItems='center'
                spacing={1.5}
                sx={{ flexWrap: 'wrap', gap: 1 }}
              >
                <Button
                  size='small'
                  color='primary'
                  variant='contained'
                  onClick={handleBlockDetailClick.bind(null, scan)}
                >
                  {t('Block detail')}
                </Button>

                <Typography variant='body2' sx={{ flexGrow: 1 }}>
                  {formatScanName(scan)}
                </Typography>

                {scan.pipelineStatus && (
                  <Chip
                    label={scan.pipelineStatus}
                    size='small'
                    color={getPipelineStatusColor(scan.pipelineStatus)}
                    sx={{ height: '24px' }}
                  />
                )}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </Paper>
  );
};
